import { Component, effect, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { filter, Subscription } from 'rxjs';
import { PageKey } from './shared/services/page/models/page.key';
import { ProgressComponent } from './shared/components/export/progress.component';
import { ThemeService } from './shared/services/theme/theme.service';
import { ToastModule } from 'primeng/toast';
import { GlobalSpinnerService } from './shared/services/global-spinner/global-spinner.service';
import { ParamsService } from './shared/services/params/params.service';
import { ButtonModule } from 'primeng/button';
import { ToastComponent } from './shared/components/toast/toast.component';
import { GlobalCacheConfig } from 'ts-cacheable';
import { PrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { HeaderItem } from './shared/components/header/models/header';
import { HeaderEnum, PermissionEnum } from '../environments/shared-environment/permission';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { PermissionService } from './shared/services/permission/permission.service';
import { MeService } from './shared/services/me/me.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LayoutComponent, HeaderComponent, ProgressComponent, ToastModule, ButtonModule, ToastComponent, PdfViewerModule],
  template: `
    <l-layout [fullscreen]="fullscreen" [foreground]="foreground" [showHeader]="showHeader" [version]="appVersion">
      @if (showHeader) {
        <l-header [headerItens]="headerItens" [foreground]="foreground" header />
      }

      <router-outlet />
    </l-layout>

    <l-toast />
    <l-progress />
  `,
})
export class AppComponent implements OnDestroy {

  constructor(
    params: ParamsService,
    themeService: ThemeService,
    globalSpinnerService: GlobalSpinnerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly primeng: PrimeNG,
    private readonly permissionService: PermissionService,
    private readonly meService: MeService
  ) {
    params.initialize();
    themeService.initialize();
    globalSpinnerService.initialize();
    this.handleRouteChange();
    this.meService.storageMe();

    GlobalCacheConfig.maxAge = 3600000;
    GlobalCacheConfig.maxCacheCount = 100;

    this.primeng.theme.set({
      preset: Aura,
      options: {
        cssLayer: {
          name: 'primeng',
          order: 'tailwind-base, primeng, tailwind-utilities'
        }
      }
    });

    this.routerSubscription = router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.handleRouteChange();
      });

    effect(() => {
      const me = this.meService.me();

      if (me)
        this.checkHeader();
    });
  }

  public fullscreen: boolean = false;

  public foreground: boolean = false;

  public showHeader: boolean = false;

  public headerItens: HeaderItem[] = [];

  public appVersion: string = `v${environment.version}`;

  private readonly routerSubscription?: Subscription;

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  private checkHeader(): void {
    const can = (resource: HeaderEnum, permission: PermissionEnum, header: HeaderItem | HeaderItem[]): HeaderItem[] => this.permissionService.has(resource, permission) ? (Array.isArray(header) ? header : [header]) : [];

    this.headerItens = [
      ...can(HeaderEnum.MANUAL_ANALYSIS, PermissionEnum.VIEW,
        {
          title: 'Análise de manuais',
          link: 'main/manual-analysis/list',
          key: PageKey.ANALYSIS
        }
      ),
      ...can(HeaderEnum.DOCS_COMPARE, PermissionEnum.VIEW, 
        {
          title: 'Comparação de documentos',
          link: 'main/documents/comparison/compare',
          key: PageKey.COMPARISON
        }
      ),
      ...can(HeaderEnum.INFORMS, PermissionEnum.VIEW, 
        {
          title: 'Informativos',
          link: 'main/informs',
          key: PageKey.INFORM
        }
      ),
      ...can(HeaderEnum.DOCS_MANAGE, PermissionEnum.VIEW, 
        {
          title: 'Gerenciar documentos',
          link: 'main/documents/manage/iatf',
          key: PageKey.MANAGE
        },
      ),
      ...can(HeaderEnum.USERS, PermissionEnum.CREATE, 
        {
          title: 'Usuários',
          link: 'main/users/list',
          key: PageKey.USERS
        },
      )
    ];
  }

  private handleRouteChange(): void {
    const foregroudRoutes = ['login', 'compare', 'users', 'manage', 'forgot/password', 'reset/password', 'first-access', 'informs'];
    const headerRoutes = ['login', 'forgot/password', 'reset/password', 'first-access'];
  
    let activeRoute = this.route;
    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild;
    }
  
    activeRoute.data.subscribe(data => {
      setTimeout(() => {
        document.title = data['title'] ? `${data['title']} | Plataforma` : 'Clarios';
      }, 100);
    });
  
    this.fullscreen = activeRoute.snapshot.data['fullscreen'] || false;
    this.foreground = foregroudRoutes.some(route => this.router.url.includes(route));
    this.showHeader = !headerRoutes.some(route => this.router.url.includes(route));
  }  

}
